import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * About modal — CartIQ 360 company story, branded in CartIQ saffron/dark
 * palette. Content comes from the provided "About Us" document verbatim.
 */
export default function AboutModal({ open, onClose }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    // Always scroll to the very top of the modal content when (re)opened,
    // otherwise the browser can restore a mid-document position.
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    });
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
      data-testid="about-modal"
    >
      <div
        ref={scrollRef}
        className="bg-[#0A0A0F] border border-[#2A2A35] max-w-3xl w-full relative max-h-[calc(100vh-24px)] md:max-h-[calc(100vh-48px)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Saffron accent header (sticky so it's always visible while scrolling) */}
        <div
          className="sticky top-0 z-20 h-[3px]"
          style={{
            background:
              "linear-gradient(90deg,#FF6B00 0%,#FF8F3F 40%,#1D9E75 100%)",
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-30 text-[#F0EDE8] hover:text-[#FF6B00] transition-colors p-2 bg-[#0A0A0F]/85 backdrop-blur border border-[#2A2A35]"
          data-testid="about-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 md:px-8 py-7 md:py-10 space-y-6">
          {/* Logo + kicker */}
          <div className="flex items-center gap-3 mb-2">
            <img
              src="/cartiq360-logo-256.png"
              alt="CartIQ 360"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#FF6B00] font-bold">
                — About CartIQ 360
              </div>
              <h1
                className="font-display uppercase text-3xl md:text-4xl leading-[1.05] text-[#F0EDE8] mt-1"
                style={{ fontFamily: "'Pirata One', serif" }}
              >
                Know <span className="text-[#FF6B00]">everything</span> about how you eat.
              </h1>
            </div>
          </div>

          <p className="text-sm md:text-base text-[#D0CFC9] leading-relaxed">
            CartIQ 360 is India's first unified food intelligence platform, helping urban
            Indian families take complete control of their food spending across every app
            they use — from Blinkit and Zepto to Swiggy, Zomato, and BigBasket — all in
            one place, in under 60 seconds.
          </p>

          <AboutSection title="The problem we solve" accent="#E24B4A">
            Urban Indian families spend ₹15,000 to ₹25,000 every month on food across
            multiple quick commerce and food delivery apps. Blinkit shows you your Blinkit
            orders. Zomato shows you your Zomato orders. But nobody shows you the complete
            picture. Every month ends with a surprise — and rarely a pleasant one.
            <span className="block mt-2 text-[#FF6B00] font-semibold">
              CartIQ 360 ends that surprise forever.
            </span>
          </AboutSection>

          <AboutSection title="How it works" accent="#FF6B00">
            Getting started takes two minutes and requires no app download. Users simply
            upload PDF invoices or screenshots of their past orders from any food app
            directly on our web platform. Our AI engine — powered by Anthropic Claude —
            reads every invoice and screenshot, extracts every item, price, and category,
            and generates a complete Grocery Intelligence Report within 60 seconds.
            <ul className="list-disc list-outside pl-5 mt-3 space-y-1 marker:text-[#FF6B00]">
              <li>Total spend broken down by category and platform</li>
              <li>Top items with reorder frequency and predicted restock dates</li>
              <li>Personalised savings opportunities — with specific rupee amounts</li>
              <li>A smart weekly grocery list tailored to purchase history and budget</li>
            </ul>
          </AboutSection>

          <AboutSection title="What makes us different" accent="#1D9E75">
            CartIQ 360 works across every major food platform in India — including apps
            that do not provide PDF invoices. Our Claude Vision technology reads order
            screenshots from Swiggy, Zomato, and Uber Eats just as accurately as PDF
            invoices from Blinkit and Zepto, including dark-mode screenshots. Users get a
            single unified view of their entire food life for the first time.
            <span className="block mt-2">
              The platform connects seamlessly with WhatsApp — where India already lives.
              Every Saturday morning at 9 AM, CartIQ 360 sends users a personalised
              grocery reminder with their smart weekly list ready to review and confirm.
              Budget alerts fire automatically at 60%, 80%, and 100% of the user's
              monthly food budget — giving them awareness and control <em>before</em>
              overspending happens, not after.
            </span>
          </AboutSection>

          <AboutSection title="Who it's for" accent="#9D3CFF">
            CartIQ 360 is built for urban Indian families and professionals aged 25 to 60
            who use quick commerce and food delivery apps regularly and want visibility,
            control, and intelligence over their food spending without changing any of
            their existing habits.
          </AboutSection>

          <AboutSection title="Pricing" accent="#FF6B00">
            A generous free tier with the first five invoice analyses at no cost. Paid
            plans start at{" "}
            <span className="font-mono-num text-[#F0EDE8] font-bold">₹499/mo</span>{" "}
            (Starter),{" "}
            <span className="font-mono-num text-[#F0EDE8] font-bold">₹999/mo</span>{" "}
            (Pro), and{" "}
            <span className="font-mono-num text-[#F0EDE8] font-bold">₹1,999/mo</span>{" "}
            (Family, up to 4 members). All paid plans include a 15-day free trial — no
            credit card required.
          </AboutSection>

          <AboutSection title="Our promise" accent="#FF6B00">
            We don't tell you to stop spending. We give you the one thing you have always
            been missing — <span className="text-[#FF6B00] font-semibold">complete awareness</span>{" "}
            of how you eat, and the confidence to stay in control.
          </AboutSection>

          <AboutSection title="Our vision — Bharat first, and beyond" accent="#1D9E75">
            CartIQ 360 is built for India first, but the hunger for food intelligence is
            universal. Our vision is to become the world's leading household food
            intelligence platform — expanding across the Middle East, Southeast Asia, and
            beyond, wherever quick commerce and food delivery are growing at the pace
            India experienced. We are building towards deep integrations with leading
            digital wallets — Google Pay, PhonePe, Paytm — enabling a seamless connection
            between what people eat and how they pay.
          </AboutSection>

          <div className="pt-4 border-t border-[#2A2A35] text-center">
            <div
              className="font-display uppercase text-2xl md:text-3xl leading-tight"
              style={{ fontFamily: "'Pirata One', serif" }}
            >
              One platform. <span className="text-[#FF6B00]">Every meal.</span>
            </div>
            <div className="text-xs md:text-sm text-[#888] mt-2 uppercase tracking-[0.18em]">
              CartIQ 360 — Born in India. Built for the World.
            </div>
            <div className="text-[10px] text-[#555] mt-3 uppercase tracking-[0.22em]">
              Solutionist ·{" "}
              <a
                href="mailto:support@solutionist.co.in"
                className="text-[#FF6B00] hover:text-[#F0EDE8]"
              >
                support@solutionist.co.in
              </a>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 btn-ghost text-xs inline-flex items-center gap-2"
              data-testid="about-modal-close-bottom"
            >
              ← Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const AboutSection = ({ title, accent, children }) => (
  <section className="border-l-[3px] pl-4 md:pl-5" style={{ borderColor: accent }}>
    <div
      className="text-[11px] uppercase tracking-[0.22em] font-bold mb-1.5"
      style={{ color: accent }}
    >
      — {title}
    </div>
    <div className="text-sm md:text-[15px] text-[#D0CFC9] leading-relaxed">{children}</div>
  </section>
);
